// src/components/communs/EvaluationRich.jsx
// ============================================================================
// Renderer d'évaluation ENRICHIE (EvaluationRich).
//
// Différences fondamentales avec Evaluation.jsx :
//
//  1. AUCUNE CORRECTION AUTOMATIQUE, JAMAIS.
//     EvaluationRich ne sert que des évaluations SOMMATIVE / EXAMEN / CONCOURS.
//     Ce composant ne calcule donc aucun score, n'affiche aucun ✅/❌, aucune
//     explication, aucun badge réussi/échoué. La note vient du rapprochement
//     manuel fait plus tard par le formateur, en base.
//     => Ne JAMAIS réintroduire de evaluateQuestion() ici.
//
//  2. Les réponses attendues ne sont pas dans les données.
//     EvaluationRichDetailDTO masque les solutions : appariement a ses valeurs
//     vidées, classement est éclaté en categories + elementsAClasser mélangés,
//     ordreAttendu devient elementsAOrdonner mélangé. Le composant s'appuie
//     uniquement sur ces champs-là.
//
//  3. Deux modes (cf. normalizeQuestion) :
//     - mode="demo"       : données brutes de l'admin (appariement/classement/
//                           ordreAttendu complets). Rien n'est transmis, pas de
//                           chronomètre serveur, pas d'appel /demarrer.
//     - mode="production" : données masquées du DTO étudiant. Chronomètre
//                           serveur via /demarrer, transmission via onSubmit.
//     La normalisation accepte LES DEUX formes, donc le même rendu sert aux
//     deux modes — c'est ce qui garantit que la démo reflète la production.
// ============================================================================
import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
    X, Clock, ChevronLeft, ChevronRight, Maximize2, Minimize2,
    AlertCircle, Send, Award, ArrowUp, ArrowDown, CheckCircle,
    Loader2, FileText, Code2, Hash, Link2, Layers, ListOrdered,
    MessageSquare, PenLine, CircleDot, ListChecks, ToggleLeft, Paperclip
} from 'lucide-react';
import { APP_CONFIG } from '../../config/appConfig';

// ---------------------------------------------------------------------------
// Métadonnées par type (aligné sur ConstruireEvaluationRich.jsx)
// ---------------------------------------------------------------------------
const TYPE_META = {
    CHOIX_UNIQUE:      { label: 'Choix unique',        Icon: CircleDot,     theme: 'blue'    },
    CHOIX_MULTIPLE:    { label: 'Choix multiple',      Icon: ListChecks,    theme: 'blue'    },
    VRAI_FAUX:         { label: 'Vrai / Faux',         Icon: ToggleLeft,    theme: 'blue'    },
    TEXTE_TROU:        { label: 'Texte à trous',       Icon: PenLine,       theme: 'purple'  },
    APPARIEMENT:       { label: 'Appariement',         Icon: Link2,         theme: 'amber'   },
    APPARIE:           { label: 'Appariement',         Icon: Link2,         theme: 'amber'   },
    CATEGORI:          { label: 'Catégorisation',      Icon: Layers,        theme: 'amber'   },
    CLASSEMENT:        { label: 'Classement',          Icon: Layers,        theme: 'amber'   },
    CLASSE:            { label: 'Classement',          Icon: Layers,        theme: 'amber'   },
    ORDONNANCEMENT:    { label: 'Ordonnancement',      Icon: ListOrdered,   theme: 'amber'   },
    REP_COURT:         { label: 'Réponse courte',      Icon: PenLine,       theme: 'emerald' },
    REP_LONG:          { label: 'Réponse longue',      Icon: FileText,      theme: 'emerald' },
    QUESTION_OUVERTE:  { label: 'Question ouverte',    Icon: MessageSquare, theme: 'emerald' },
    ZONE_TEXTE_RICHE:  { label: 'Zone de texte riche', Icon: FileText,      theme: 'emerald' },
    REP_NUMERIQUE:     { label: 'Réponse numérique',   Icon: Hash,          theme: 'indigo'  },
    CODE:              { label: 'Code',                Icon: Code2,         theme: 'indigo'  },
    DEPOT_FICHIER:     { label: 'Dépôt de fichier',    Icon: Paperclip,     theme: 'rose'    }
};

const THEMES = {
    blue:    { chip: 'bg-blue-50 text-blue-700 border-blue-200',       accent: 'border-l-blue-500',    ring: 'focus:ring-blue-500',    sel: 'border-blue-500 bg-blue-50',       dot: 'bg-blue-500'    },
    purple:  { chip: 'bg-purple-50 text-purple-700 border-purple-200', accent: 'border-l-purple-500',  ring: 'focus:ring-purple-500',  sel: 'border-purple-500 bg-purple-50',   dot: 'bg-purple-500'  },
    amber:   { chip: 'bg-amber-50 text-amber-700 border-amber-200',    accent: 'border-l-amber-500',   ring: 'focus:ring-amber-500',   sel: 'border-amber-500 bg-amber-50',     dot: 'bg-amber-500'   },
    emerald: { chip: 'bg-emerald-50 text-emerald-700 border-emerald-200', accent: 'border-l-emerald-500', ring: 'focus:ring-emerald-500', sel: 'border-emerald-500 bg-emerald-50', dot: 'bg-emerald-500' },
    indigo:  { chip: 'bg-indigo-50 text-indigo-700 border-indigo-200', accent: 'border-l-indigo-500',  ring: 'focus:ring-indigo-500',  sel: 'border-indigo-500 bg-indigo-50',   dot: 'bg-indigo-500'  },
    rose:    { chip: 'bg-rose-50 text-rose-700 border-rose-200',       accent: 'border-l-rose-500',    ring: 'focus:ring-rose-500',    sel: 'border-rose-500 bg-rose-50',       dot: 'bg-rose-500'    },
    slate:   { chip: 'bg-slate-50 text-slate-700 border-slate-200',    accent: 'border-l-slate-400',   ring: 'focus:ring-slate-500',   sel: 'border-slate-500 bg-slate-50',     dot: 'bg-slate-500'   }
};

const getMeta = (type) => TYPE_META[type] || { label: type || 'Question', Icon: AlertCircle, theme: 'slate' };
const getTheme = (type) => THEMES[getMeta(type).theme] || THEMES.slate;

// Familles de types (évite de répéter les listes partout)
const IS_APPARIEMENT = (t) => ['APPARIEMENT', 'APPARIE', 'CATEGORI'].includes(t);
const IS_CLASSEMENT  = (t) => ['CLASSEMENT', 'CLASSE'].includes(t);
const IS_TEXTE_LIBRE = (t) => ['REP_COURT', 'REP_LONG', 'QUESTION_OUVERTE', 'ZONE_TEXTE_RICHE'].includes(t);

// ---------------------------------------------------------------------------
// Mélange déterministe (une seule fois par question, stable entre re-renders)
// ---------------------------------------------------------------------------
const shuffleArray = (arr) => {
    const out = [...arr];
    for (let i = out.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [out[i], out[j]] = [out[j], out[i]];
    }
    return out;
};

// ---------------------------------------------------------------------------
// Normalisation d'une question — accepte la forme ADMIN (démo) ET la forme
// DTO étudiant (production), pour que les deux modes rendent exactement pareil.
//
//  APPARIEMENT : admin -> appariement {cle: valeurAttendue}
//                prod  -> appariement {cle: ""}          (valeurs masquées)
//                => on ne garde que les CLÉS dans les deux cas.
//
//  CLASSEMENT  : admin -> classement {categorie: [elements]}
//                prod  -> categories [] + elementsAClasser [] (déjà mélangés)
//                => on reconstruit categories + elements dans les deux cas.
//
//  ORDONNANCEMENT : admin -> ordreAttendu [] (DANS L'ORDRE CORRECT !)
//                   prod  -> elementsAOrdonner [] (mélangés)
//                => en démo on mélange nous-mêmes, sinon la démo afficherait
//                   la solution toute faite et ne refléterait pas la production.
// ---------------------------------------------------------------------------
const normalizeQuestion = (q) => {
    const type = q.type;
    const norm = { ...q };

    if (IS_APPARIEMENT(type)) {
        norm._elementsAApparier = Object.keys(q.appariement || {});
    }

    if (IS_CLASSEMENT(type)) {
        if (Array.isArray(q.categories) && Array.isArray(q.elementsAClasser)) {
            norm._categories = q.categories;
            norm._elementsAClasser = q.elementsAClasser;
        } else if (q.classement && typeof q.classement === 'object') {
            norm._categories = Object.keys(q.classement);
            norm._elementsAClasser = shuffleArray(
                Object.values(q.classement).flat().filter(Boolean)
            );
        } else {
            norm._categories = [];
            norm._elementsAClasser = [];
        }
    }

    if (type === 'ORDONNANCEMENT') {
        if (Array.isArray(q.elementsAOrdonner)) {
            norm._elementsAOrdonner = q.elementsAOrdonner;
        } else if (Array.isArray(q.ordreAttendu)) {
            norm._elementsAOrdonner = shuffleArray(q.ordreAttendu);
        } else {
            norm._elementsAOrdonner = [];
        }
    }

    if (type === 'TEXTE_TROU') {
        const matches = String(q.enonce || '').match(/___/g);
        norm._nbTrous = matches ? matches.length : 0;
    }

    return norm;
};

const formatTime = (s) => {
    const total = Math.max(0, Math.floor(s || 0));
    const h = Math.floor(total / 3600);
    const m = Math.floor((total % 3600) / 60);
    const sec = total % 60;
    const pad = (n) => String(n).padStart(2, '0');
    return h > 0 ? `${pad(h)}:${pad(m)}:${pad(sec)}` : `${pad(m)}:${pad(sec)}`;
};

// ---------------------------------------------------------------------------
// Une réponse est-elle considérée comme fournie ?
// ---------------------------------------------------------------------------
const hasAnswer = (value) => {
    if (value === undefined || value === null) return false;
    if (Array.isArray(value)) return value.some(v => v !== null && v !== undefined && String(v).trim() !== '');
    if (typeof value === 'object') return Object.values(value).some(v => v !== null && v !== undefined && String(v).trim() !== '');
    return String(value).trim() !== '';
};

export default function EvaluationRich({
    evaluation,
    formationTitre,
    moduleOrdre = 1,
    totalModules = 1,
    onClose,
    isOpen = true,
    onSubmit,
	// eslint-disable-next-line
    onSubmitSuccess,
    submitting = false,
    resultat,
    evaluationId,
    etudiantId,
    mode = 'production',
    token
}) {
    const isDemo = mode === 'demo';

    const [currentIndex, setCurrentIndex] = useState(0);
    const [answers, setAnswers] = useState({});
    const [showWelcome, setShowWelcome] = useState(true);
    const [examStarted, setExamStarted] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [timeSpent, setTimeSpent] = useState(0);

    // Session serveur (production uniquement)
    const [sessionDateLimite, setSessionDateLimite] = useState(null);
    const [remainingSeconds, setRemainingSeconds] = useState(0);
    const [isSessionLoaded, setIsSessionLoaded] = useState(false);
    const [sessionError, setSessionError] = useState(null);
    const [hasAutoSubmitted, setHasAutoSubmitted] = useState(false);

    const containerRef = useRef(null);
    const timerRef = useRef(null);

    // Les questions sont normalisées UNE SEULE FOIS (le mélange interne ne doit
    // pas rejouer à chaque frappe de l'étudiant).
    const questions = useMemo(
        () => (evaluation?.questions || []).map(normalizeQuestion),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [evaluation?.id, evaluation?.questions?.length]
    );

    const totalQuestions = questions.length;
    const currentQuestion = questions[currentIndex];
    const answeredCount = useMemo(
        () => questions.filter((_, i) => hasAnswer(answers[i])).length,
        [answers, questions]
    );

    // -----------------------------------------------------------------------
    // Réinitialisation complète quand on (r)ouvre en démo : la démo doit
    // pouvoir être rejouée indéfiniment sans état résiduel.
    // -----------------------------------------------------------------------
    useEffect(() => {
        if (isDemo && isOpen) {
            setCurrentIndex(0);
            setAnswers({});
            setShowWelcome(true);
            setExamStarted(false);
            setIsSubmitted(false);
            setTimeSpent(0);
            setSessionDateLimite(null);
            setRemainingSeconds(0);
            setIsSessionLoaded(false);
            setSessionError(null);
            setHasAutoSubmitted(false);
        }
    }, [isOpen, isDemo]);

    // -----------------------------------------------------------------------
    // /demarrer — production uniquement. En démo on ne touche jamais au serveur.
    // -----------------------------------------------------------------------
    useEffect(() => {
        if (!isOpen || !examStarted || isSessionLoaded) return;

        if (isDemo || !etudiantId || !evaluationId) {
            setIsSessionLoaded(true);
            return;
        }

        if (!token) {
            console.error("❌ [EvaluationRich] Aucun token : impossible d'appeler /demarrer.");
            setSessionError("Session non démarrée (authentification manquante).");
            setIsSessionLoaded(true);
            return;
        }

        const demarrer = async () => {
            try {
                const res = await fetch(
                    `${APP_CONFIG.BASE_URL}/api/v1/etudiant/rich/evaluations/${evaluationId}/demarrer?etudiantId=${etudiantId}`,
                    {
                        method: 'POST',
                        headers: {
                            'Authorization': `Bearer ${token}`,
                            'Content-Type': 'application/json'
                        }
                    }
                );
                if (!res.ok) throw new Error(`Erreur HTTP ${res.status}`);
                const data = await res.json();
                if (data?.dateLimite) {
                    const limite = new Date(data.dateLimite);
                    setSessionDateLimite(limite);
                    setRemainingSeconds(Math.max(0, Math.floor((limite - new Date()) / 1000)));
                }
            } catch (err) {
                console.error('[EvaluationRich] Erreur /demarrer :', err);
                setSessionError("Le chronomètre n'a pas pu être démarré.");
            } finally {
                setIsSessionLoaded(true);
            }
        };
        demarrer();
    }, [isOpen, examStarted, isSessionLoaded, isDemo, etudiantId, evaluationId, token]);

    // -----------------------------------------------------------------------
    // Chronomètre : compte à rebours si le serveur a fixé une limite,
    // sinon simple temps écoulé (et toujours simple temps écoulé en démo).
    // -----------------------------------------------------------------------
    useEffect(() => {
        if (!isOpen || !examStarted || isSubmitted || !isSessionLoaded) return;

        timerRef.current = setInterval(() => {
            setTimeSpent(prev => prev + 1);
            if (sessionDateLimite) {
                setRemainingSeconds(Math.max(0, Math.floor((sessionDateLimite - new Date()) / 1000)));
            }
        }, 1000);

        return () => clearInterval(timerRef.current);
    }, [isOpen, examStarted, isSubmitted, isSessionLoaded, sessionDateLimite]);

    // Soumission automatique à l'expiration du délai (production seulement)
    useEffect(() => {
        if (isDemo || !sessionDateLimite || isSubmitted || hasAutoSubmitted) return;
        if (remainingSeconds <= 0 && isSessionLoaded && examStarted) {
            setHasAutoSubmitted(true);
            doSubmit(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [remainingSeconds, sessionDateLimite, isSubmitted, hasAutoSubmitted, isSessionLoaded, examStarted, isDemo]);

    // -----------------------------------------------------------------------
    // Saisie
    // -----------------------------------------------------------------------
    const setAnswer = (index, value) => {
        if (isSubmitted) return;
        setAnswers(prev => ({ ...prev, [index]: value }));
    };

    const toggleMultiple = (index, optIndex) => {
        if (isSubmitted) return;
        setAnswers(prev => {
            const current = Array.isArray(prev[index]) ? prev[index] : [];
            const key = String(optIndex);
            const next = current.includes(key) ? current.filter(v => v !== key) : [...current, key];
            return { ...prev, [index]: next };
        });
    };

    // -----------------------------------------------------------------------
    // Construction du payload serveur.
    // Format attendu : [{ questionIndex, reponse }] — "reponse" correspond à
    // ReponseQCMRichDTO.getReponse() côté backend (Object libre : String, List
    // ou Map selon le type de question).
    // -----------------------------------------------------------------------
    const buildPayload = () => questions.map((q, index) => {
        const raw = answers[index];
        let reponse;

        if (q.type === 'CHOIX_UNIQUE' || q.type === 'VRAI_FAUX') {
            // Index de l'option, en chaîne (le backend fait un parseInt)
            reponse = raw !== undefined && raw !== null ? String(raw) : '';
        } else if (q.type === 'CHOIX_MULTIPLE') {
            reponse = Array.isArray(raw) ? raw.map(String) : [];
        } else if (q.type === 'ORDONNANCEMENT') {
            // Liste ordonnée telle que l'étudiant l'a arrangée
            reponse = Array.isArray(raw) ? raw : (q._elementsAOrdonner || []);
        } else if (q.type === 'TEXTE_TROU' || IS_APPARIEMENT(q.type) || IS_CLASSEMENT(q.type)) {
            // Map : {indexTrou: texte} / {cle: valeur} / {element: categorie}
            reponse = (raw && typeof raw === 'object' && !Array.isArray(raw)) ? raw : {};
        } else {
            // Texte libre, numérique, code, fichier
            reponse = raw !== undefined && raw !== null ? String(raw) : '';
        }

        return { questionIndex: index, reponse };
    });

    const doSubmit = (auto = false) => {
        if (isSubmitted) return;

        if (isDemo) {
            // ⭐ En démo, on ne transmet RIEN : c'est une simple démonstration.
            setIsSubmitted(true);
            if (timerRef.current) clearInterval(timerRef.current);
            return;
        }

        if (!auto && answeredCount < totalQuestions) {
            const reste = totalQuestions - answeredCount;
            const ok = window.confirm(
                `Il reste ${reste} question${reste > 1 ? 's' : ''} sans réponse. Soumettre quand même ?`
            );
            if (!ok) return;
        }

        setIsSubmitted(true);
        if (timerRef.current) clearInterval(timerRef.current);

        if (onSubmit) onSubmit(buildPayload());
    };

    const goToNext = () => setCurrentIndex(i => Math.min(i + 1, totalQuestions - 1));
    const goToPrev = () => setCurrentIndex(i => Math.max(i - 1, 0));

    const toggleFullscreen = () => {
        if (!document.fullscreenElement) {
            containerRef.current?.requestFullscreen?.();
            setIsFullscreen(true);
        } else {
            document.exitFullscreen?.();
            setIsFullscreen(false);
        }
    };

    // =======================================================================
    // RENDU PAR TYPE DE QUESTION
    // =======================================================================
    const renderMedia = (q) => {
        if (!q.mediaUrl) return null;
        const t = (q.mediaType || '').toUpperCase();
        const common = 'w-full rounded-lg border border-gray-200 mb-4';
        if (t === 'IMAGE') {
            return <img src={q.mediaUrl} alt="" className={`${common} max-h-72 object-contain bg-gray-50`} />;
        }
        if (t === 'AUDIO') {
            return <audio src={q.mediaUrl} controls className={`${common} p-2 bg-gray-50`} />;
        }
        if (t === 'VIDEO') {
            return <video src={q.mediaUrl} controls className={`${common} max-h-80 bg-black`} />;
        }
        if (t === 'PDF') {
            return (
                <div className={`${common} overflow-hidden`}>
                    <object data={q.mediaUrl} type="application/pdf" className="w-full h-64 sm:h-96">
                        <div className="p-4 text-center bg-gray-50">
                            <p className="text-sm text-gray-600 mb-2">
                                Aperçu PDF indisponible dans ce navigateur.
                            </p>
                            <a href={q.mediaUrl} target="_blank" rel="noopener noreferrer"
                               className="inline-flex items-center gap-2 text-sm text-blue-600 hover:underline">
                                <FileText className="w-4 h-4" /> Ouvrir le PDF dans un nouvel onglet
                            </a>
                        </div>
                    </object>
                </div>
            );
        }
        return (
            <a href={q.mediaUrl} target="_blank" rel="noopener noreferrer"
               className="inline-flex items-center gap-2 text-sm text-blue-600 hover:underline mb-4">
                <FileText className="w-4 h-4" /> Ouvrir le document joint
            </a>
        );
    };

    const renderChoix = (q, index, multiple) => {
        const theme = getTheme(q.type);
        const raw = answers[index];
        return (
            <div className="space-y-2">
                {(q.options || []).map((opt, oi) => {
                    const selected = multiple
                        ? Array.isArray(raw) && raw.includes(String(oi))
                        : String(raw) === String(oi);
                    return (
                        <button
                            key={oi}
                            type="button"
                            onClick={() => multiple ? toggleMultiple(index, oi) : setAnswer(index, String(oi))}
                            disabled={isSubmitted}
                            className={`w-full text-left p-3 sm:p-4 rounded-xl border-2 transition-all flex items-center gap-3 disabled:opacity-60 ${
                                selected ? theme.sel : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                            }`}
                        >
                            <span className={`flex-shrink-0 w-6 h-6 border-2 flex items-center justify-center ${
                                multiple ? 'rounded-md' : 'rounded-full'
                            } ${selected ? `${theme.dot} border-transparent` : 'border-gray-300'}`}>
                                {selected && <CheckCircle className="w-4 h-4 text-white" />}
                            </span>
                            <span className="text-xs sm:text-base text-gray-800 font-serif">{opt}</span>
                        </button>
                    );
                })}
            </div>
        );
    };

    const renderVraiFaux = (q, index) => {
        const theme = getTheme(q.type);
        const raw = answers[index];
        const options = (q.options && q.options.length === 2) ? q.options : ['Vrai', 'Faux'];
        return (
            <div className="grid grid-cols-2 gap-3">
                {options.map((label, oi) => {
                    const selected = String(raw) === String(oi);
                    return (
                        <button
                            key={oi}
                            type="button"
                            onClick={() => setAnswer(index, String(oi))}
                            disabled={isSubmitted}
                            className={`p-4 sm:p-6 rounded-xl border-2 font-serif text-sm sm:text-lg font-semibold transition-all disabled:opacity-60 ${
                                selected ? theme.sel : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50 text-gray-700'
                            }`}
                        >
                            {label}
                        </button>
                    );
                })}
            </div>
        );
    };

    const renderTexteTrou = (q, index) => {
        const theme = getTheme(q.type);
        const map = (answers[index] && typeof answers[index] === 'object') ? answers[index] : {};
        const nb = q._nbTrous || 0;

        if (nb === 0) {
            return (
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg text-sm">
                    <p className="text-amber-700 font-semibold">Aucun trou détecté</p>
                    <p className="text-gray-600 text-xs mt-1">
                        L'énoncé doit contenir des marqueurs <code className="bg-white px-1 rounded">___</code>.
                    </p>
                </div>
            );
        }

        // L'énoncé est découpé sur ___ pour insérer les champs en ligne.
        const segments = String(q.enonce || '').split('___');
        return (
            <div className={`p-4 rounded-xl bg-gray-50 border-l-4 ${theme.accent} leading-loose text-sm sm:text-base font-serif text-gray-800`}>
                {segments.map((seg, si) => (
                    <React.Fragment key={si}>
                        <span>{seg}</span>
                        {si < segments.length - 1 && (
                            <input
                                type="text"
                                value={map[String(si)] || ''}
                                onChange={(e) => setAnswer(index, { ...map, [String(si)]: e.target.value })}
                                disabled={isSubmitted}
                                placeholder={`Trou ${si + 1}`}
                                className={`inline-block mx-1 px-2 py-1 w-32 sm:w-40 border-b-2 border-gray-400 bg-white rounded-t text-sm focus:outline-none ${theme.ring} focus:ring-2 disabled:opacity-60`}
                            />
                        )}
                    </React.Fragment>
                ))}
            </div>
        );
    };

    const renderAppariement = (q, index) => {
        const theme = getTheme(q.type);
        const elements = q._elementsAApparier || [];
        const map = (answers[index] && typeof answers[index] === 'object') ? answers[index] : {};

        if (elements.length === 0) {
            return (
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg text-sm">
                    <p className="text-amber-700 font-semibold">Appariement non disponible</p>
                    <p className="text-gray-600 text-xs mt-1">Aucun élément à associer n'a été transmis pour cette question.</p>
                </div>
            );
        }

        return (
            <div className="space-y-2">
                {elements.map((el) => (
                    <div key={el} className={`flex flex-wrap items-center gap-2 p-3 rounded-xl bg-white border-2 border-gray-200 border-l-4 ${theme.accent}`}>
                        <span className="text-xs sm:text-sm font-serif font-semibold text-gray-800 min-w-[80px] sm:min-w-[140px]">
                            {el}
                        </span>
                        <span className="text-gray-400">→</span>
                        <input
                            type="text"
                            value={map[el] || ''}
                            onChange={(e) => setAnswer(index, { ...map, [el]: e.target.value })}
                            disabled={isSubmitted}
                            placeholder="Votre association…"
                            className={`flex-1 min-w-[120px] px-3 py-2 border-2 border-gray-200 rounded-lg text-xs sm:text-sm font-serif focus:outline-none focus:ring-2 ${theme.ring} disabled:opacity-60`}
                        />
                    </div>
                ))}
            </div>
        );
    };

    const renderClassement = (q, index) => {
        const theme = getTheme(q.type);
        const elements = q._elementsAClasser || [];
        const categories = q._categories || [];
        const map = (answers[index] && typeof answers[index] === 'object') ? answers[index] : {};

        if (elements.length === 0 || categories.length === 0) {
            return (
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg text-sm">
                    <p className="text-amber-700 font-semibold">Classement non disponible</p>
                    <p className="text-gray-600 text-xs mt-1">
                        Les catégories ou les éléments à classer n'ont pas été transmis pour cette question.
                    </p>
                </div>
            );
        }

        return (
            <div className="space-y-2">
                <div className="flex flex-wrap gap-1.5 mb-3">
                    {categories.map(cat => (
                        <span key={cat} className={`text-[10px] sm:text-xs px-2.5 py-1 rounded-full border ${theme.chip} font-semibold`}>
                            {cat}
                        </span>
                    ))}
                </div>
                {elements.map((el) => (
                    <div key={el} className={`flex flex-wrap items-center gap-2 p-3 rounded-xl bg-white border-2 border-gray-200 border-l-4 ${theme.accent}`}>
                        <span className="text-xs sm:text-sm font-serif font-semibold text-gray-800 min-w-[80px] sm:min-w-[140px]">
                            {el}
                        </span>
                        <span className="text-gray-400">→</span>
                        <select
                            value={map[el] || ''}
                            onChange={(e) => setAnswer(index, { ...map, [el]: e.target.value })}
                            disabled={isSubmitted}
                            className={`flex-1 min-w-[140px] px-3 py-2 border-2 border-gray-200 rounded-lg text-xs sm:text-sm bg-white font-serif focus:outline-none focus:ring-2 ${theme.ring} disabled:opacity-60`}
                        >
                            <option value="">Choisir une catégorie…</option>
                            {categories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                        </select>
                    </div>
                ))}
            </div>
        );
    };

    const renderOrdonnancement = (q, index) => {
        const theme = getTheme(q.type);
        const base = q._elementsAOrdonner || [];
        const ordre = Array.isArray(answers[index]) && answers[index].length === base.length
            ? answers[index]
            : base;

        if (base.length === 0) {
            return (
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg text-sm">
                    <p className="text-amber-700 font-semibold">Ordonnancement non disponible</p>
                    <p className="text-gray-600 text-xs mt-1">Aucun élément à ordonner n'a été transmis pour cette question.</p>
                </div>
            );
        }

        const move = (from, delta) => {
            const to = from + delta;
            if (to < 0 || to >= ordre.length) return;
            const next = [...ordre];
            [next[from], next[to]] = [next[to], next[from]];
            setAnswer(index, next);
        };

        return (
            <div className="space-y-2">
                <p className="text-[10px] sm:text-xs text-gray-500 mb-2">
                    Utilisez les flèches pour remettre les éléments dans le bon ordre.
                </p>
                {ordre.map((el, i) => (
                    <div key={`${el}-${i}`} className={`flex items-center gap-2 p-3 rounded-xl bg-white border-2 border-gray-200 border-l-4 ${theme.accent}`}>
                        <span className={`flex-shrink-0 w-7 h-7 rounded-full ${theme.dot} text-white text-xs font-bold flex items-center justify-center`}>
                            {i + 1}
                        </span>
                        <span className="flex-1 text-xs sm:text-sm font-serif text-gray-800">{el}</span>
                        <button
                            type="button" onClick={() => move(i, -1)} disabled={isSubmitted || i === 0}
                            className="p-1.5 text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-lg disabled:opacity-25"
                        >
                            <ArrowUp className="w-4 h-4" />
                        </button>
                        <button
                            type="button" onClick={() => move(i, 1)} disabled={isSubmitted || i === ordre.length - 1}
                            className="p-1.5 text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-lg disabled:opacity-25"
                        >
                            <ArrowDown className="w-4 h-4" />
                        </button>
                    </div>
                ))}
            </div>
        );
    };

    const renderTexteLibre = (q, index) => {
        const theme = getTheme(q.type);
        const rows = q.type === 'REP_COURT' ? 2 : (q.type === 'ZONE_TEXTE_RICHE' ? 10 : 7);
        const value = answers[index] !== undefined ? String(answers[index]) : '';
        return (
            <div>
                <textarea
                    value={value}
                    onChange={(e) => setAnswer(index, e.target.value)}
                    disabled={isSubmitted}
                    rows={rows}
                    placeholder="Rédigez votre réponse ici…"
                    className={`w-full p-3 sm:p-4 border-2 border-gray-200 rounded-xl font-serif text-xs sm:text-base text-gray-800 focus:outline-none focus:ring-2 ${theme.ring} disabled:opacity-60`}
                />
                <p className="text-[10px] text-gray-400 mt-1 text-right">{value.trim().split(/\s+/).filter(Boolean).length} mot(s)</p>
            </div>
        );
    };

    const renderNumerique = (q, index) => {
        const theme = getTheme(q.type);
        return (
            <input
                type="number"
                step="any"
                value={answers[index] !== undefined ? answers[index] : ''}
                onChange={(e) => setAnswer(index, e.target.value)}
                disabled={isSubmitted}
                placeholder="Votre réponse numérique"
                className={`w-full sm:w-64 p-3 sm:p-4 border-2 border-gray-200 rounded-xl font-mono text-base sm:text-xl text-gray-800 focus:outline-none focus:ring-2 ${theme.ring} disabled:opacity-60`}
            />
        );
    };

    const renderCode = (q, index) => (
        <div>
            <div className="flex items-center justify-between px-3 py-2 bg-gray-800 rounded-t-xl">
                <span className="text-[10px] sm:text-xs font-mono text-gray-300">
                    {q.codeLangage || 'code'}
                </span>
                <Code2 className="w-4 h-4 text-gray-400" />
            </div>
            <textarea
                value={answers[index] !== undefined ? String(answers[index]) : ''}
                onChange={(e) => setAnswer(index, e.target.value)}
                disabled={isSubmitted}
                rows={12}
                spellCheck={false}
                placeholder="// Écrivez votre code ici"
                className="w-full p-3 sm:p-4 rounded-b-xl font-mono text-[11px] sm:text-sm bg-gray-900 text-green-300 border-2 border-t-0 border-gray-800 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-60"
            />
        </div>
    );

    const renderFichier = (q, index) => {
        const theme = getTheme(q.type);
        const meta = q.metaDonnees || {};
        const accepted = meta.acceptedTypes || ['audio/*'];
        const maxSize = meta.maxFileSize || 5242880;
        const value = answers[index];

        return (
            <div className={`p-5 sm:p-6 border-2 border-dashed border-gray-300 rounded-xl text-center bg-gray-50`}>
                <Paperclip className="w-8 h-8 sm:w-10 sm:h-10 text-gray-400 mx-auto mb-3" />
                <p className="text-xs sm:text-sm text-gray-600 font-serif mb-1">
                    Déposez votre fichier de réponse
                </p>
                <p className="text-[10px] text-gray-400 mb-3">
                    {accepted.join(', ')} — max {(maxSize / 1048576).toFixed(1)} Mo
                </p>
                <input
                    type="file"
                    accept={accepted.join(',')}
                    disabled={isSubmitted}
                    onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        if (file.size > maxSize) {
                            alert(`Fichier trop volumineux (max ${(maxSize / 1048576).toFixed(1)} Mo).`);
                            e.target.value = '';
                            return;
                        }
                        // ⚠️ Seul le NOM est conservé : l'upload réel doit passer
                        // par un endpoint dédié (non fourni dans le contrôleur).
                        setAnswer(index, file.name);
                    }}
                    className={`block w-full text-xs text-gray-600 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-xs file:font-semibold file:bg-white file:border file:border-gray-300 file:text-gray-700 hover:file:bg-gray-100 disabled:opacity-60`}
                />
                {value && (
                    <p className={`mt-3 text-xs font-semibold px-3 py-1.5 rounded-full inline-block border ${theme.chip}`}>
                        {value}
                    </p>
                )}
            </div>
        );
    };

    const renderAnswerZone = (q, index) => {
        switch (q.type) {
            case 'CHOIX_UNIQUE':     return renderChoix(q, index, false);
            case 'CHOIX_MULTIPLE':   return renderChoix(q, index, true);
            case 'VRAI_FAUX':        return renderVraiFaux(q, index);
            case 'TEXTE_TROU':       return renderTexteTrou(q, index);
            case 'APPARIEMENT':
            case 'APPARIE':
            case 'CATEGORI':         return renderAppariement(q, index);
            case 'CLASSEMENT':
            case 'CLASSE':           return renderClassement(q, index);
            case 'ORDONNANCEMENT':   return renderOrdonnancement(q, index);
            case 'REP_COURT':
            case 'REP_LONG':
            case 'QUESTION_OUVERTE':
            case 'ZONE_TEXTE_RICHE': return renderTexteLibre(q, index);
            case 'REP_NUMERIQUE':    return renderNumerique(q, index);
            case 'CODE':             return renderCode(q, index);
            case 'DEPOT_FICHIER':    return renderFichier(q, index);
            default:
                return (
                    <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-600">
                        Type de question non pris en charge : <strong>{q.type}</strong>
                    </div>
                );
        }
    };

    const renderQuestion = (q, index) => {
        const meta = getMeta(q.type);
        const theme = getTheme(q.type);
        const Icon = meta.Icon;
        const answered = hasAnswer(answers[index]);

        return (
            <div className={`bg-white rounded-2xl shadow-sm border border-gray-200 border-l-4 ${theme.accent} p-4 sm:p-7`}>
                <div className="flex flex-wrap items-center gap-2 mb-4">
                    <span className={`inline-flex items-center gap-1.5 text-[10px] sm:text-xs font-semibold px-2.5 py-1 rounded-full border ${theme.chip}`}>
                        <Icon className="w-3 h-3 sm:w-3.5 sm:h-3.5" /> {meta.label}
                    </span>
                    {q.points > 0 && (
                        <span className="text-[10px] sm:text-xs text-gray-600 bg-gray-100 px-2.5 py-1 rounded-full font-semibold">
                            {q.points} pt{q.points > 1 ? 's' : ''}
                        </span>
                    )}
                    <span className={`ml-auto text-[10px] sm:text-xs px-2.5 py-1 rounded-full font-semibold ${
                        answered ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-gray-100 text-gray-500'
                    }`}>
                        {answered ? 'Répondu' : 'Sans réponse'}
                    </span>
                </div>

                {renderMedia(q)}

                {/* Pour TEXTE_TROU l'énoncé est rendu par la zone de réponse (champs en ligne) */}
                {q.type !== 'TEXTE_TROU' && (
                    <p className="text-sm sm:text-xl font-serif text-gray-900 mb-5 sm:mb-6 leading-relaxed">
                        {q.enonce}
                    </p>
                )}

                {renderAnswerZone(q, index)}
            </div>
        );
    };

    // =======================================================================
    // ÉCRANS
    // =======================================================================
    const renderWelcome = () => (
        <div className="flex flex-col items-center justify-center h-full min-h-[400px] sm:min-h-[500px] text-center p-4 sm:p-8">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 sm:p-10 max-w-3xl w-full">
                <div className="flex justify-center mb-4 sm:mb-6">
                    <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-green-50 border border-green-200 flex items-center justify-center">
                        <Award className="w-9 h-9 sm:w-11 sm:h-11 text-green-800" />
                    </div>
                </div>

                <h1 className="text-xl sm:text-3xl font-serif font-bold text-gray-900 mb-2">
                    {evaluation?.titre || 'Évaluation'}
                </h1>
                <p className="text-sm sm:text-lg text-gray-600 font-serif mb-2">
                    Évaluation sur épreuve — correction par votre formateur
                </p>
                {isDemo && (
                    <p className="inline-block text-[10px] sm:text-xs font-semibold px-3 py-1 rounded-full bg-amber-50 text-amber-700 border border-amber-200 mb-4">
                        MODE DÉMONSTRATION — aucune réponse ne sera enregistrée
                    </p>
                )}
                {evaluation?.description && (
                    <p className="text-xs sm:text-base text-gray-700 font-serif mb-5">{evaluation.description}</p>
                )}

                <div className="bg-gray-50 rounded-xl p-4 sm:p-6 mb-6 text-left">
                    <div className="flex justify-between text-xs sm:text-base py-2 border-b border-gray-200">
                        <span className="text-gray-600 font-serif">Questions</span>
                        <span className="text-gray-900 font-bold">{totalQuestions}</span>
                    </div>
                    <div className="flex justify-between text-xs sm:text-base py-2 border-b border-gray-200">
                        <span className="text-gray-600 font-serif">Durée allouée</span>
                        <span className="text-gray-900 font-bold">
                            {evaluation?.dureeMinutes ? `${evaluation.dureeMinutes} min` : 'Non limitée'}
                        </span>
                    </div>
                    <div className="flex justify-between text-xs sm:text-base py-2">
                        <span className="text-gray-600 font-serif">Total des points</span>
                        <span className="text-gray-900 font-bold">
                            {questions.reduce((acc, q) => acc + (q.points || 0), 0)}
                        </span>
                    </div>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 sm:p-4 mb-6 text-left">
                    <p className="text-[11px] sm:text-sm text-blue-900 font-serif">
                        Vos réponses seront transmises telles quelles à votre formateur.
                        Aucun score ne s'affichera à la fin de l'épreuve : le résultat vous
                        sera communiqué après correction.
                    </p>
                </div>

                <button
                    onClick={() => {
                        if (totalQuestions === 0) {
                            alert("Cette évaluation ne contient aucune question.");
                            return;
                        }
                        setShowWelcome(false);
                        setExamStarted(true);
                    }}
                    className="w-full py-3 sm:py-4 bg-green-800 text-white rounded-xl hover:bg-green-700 transition font-serif font-semibold text-sm sm:text-lg"
                >
                    Commencer l'évaluation
                </button>
            </div>
        </div>
    );

    // ⭐ Écran de fin : JAMAIS de score, JAMAIS de badge réussi/échoué.
    const renderEndScreen = () => {
        const totalPoints = questions.reduce((acc, q) => acc + (q.points || 0), 0);
        return (
            <div className="space-y-4 sm:space-y-6 max-w-3xl mx-auto">
                <div className="bg-white rounded-2xl shadow-sm border border-gray-200 border-t-4 border-t-green-700 p-6 sm:p-10 text-center">
                    <div className="flex justify-center mb-4 sm:mb-6">
                        <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-green-50 border border-green-200 flex items-center justify-center">
                            {submitting
                                ? <Loader2 className="w-9 h-9 sm:w-11 sm:h-11 text-green-700 animate-spin" />
                                : <CheckCircle className="w-9 h-9 sm:w-11 sm:h-11 text-green-700" />}
                        </div>
                    </div>

                    <h2 className="text-lg sm:text-2xl font-serif font-bold text-gray-900 mb-2">
                        {isDemo
                            ? 'Démonstration terminée'
                            : submitting ? 'Transmission en cours…' : 'Réponses transmises'}
                    </h2>
                    <p className="text-sm sm:text-lg text-gray-600 font-serif mb-6">
                        {isDemo
                            ? "Aucune réponse n'a été enregistrée : il s'agissait d'une simple démonstration."
                            : "Le résultat de cette évaluation vous sera communiqué après correction par votre formateur."}
                    </p>

                    <div className="flex flex-wrap items-center justify-center gap-5 sm:gap-10">
                        <div>
                            <div className="text-xl sm:text-3xl font-bold text-gray-900">
                                {answeredCount}/{totalQuestions}
                            </div>
                            <p className="text-[10px] sm:text-sm text-gray-500">Questions répondues</p>
                        </div>
                        <div className="w-px h-12 sm:h-14 bg-gray-200" />
                        <div>
                            <div className="text-xl sm:text-3xl font-bold text-gray-900">{totalPoints}</div>
                            <p className="text-[10px] sm:text-sm text-gray-500">Points en jeu</p>
                        </div>
                        <div className="w-px h-12 sm:h-14 bg-gray-200" />
                        <div>
                            <div className="text-xl sm:text-3xl font-bold text-gray-900">{formatTime(timeSpent)}</div>
                            <p className="text-[10px] sm:text-sm text-gray-500">Temps passé</p>
                        </div>
                    </div>

                    {/* Le serveur peut renvoyer un statut (ex. EN_ATTENTE_CORRECTION).
                        On n'affiche QUE ce statut, jamais un score. */}
                    {!isDemo && resultat?.statut && (
                        <p className="mt-6 inline-block text-[10px] sm:text-xs font-semibold px-3 py-1.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200">
                            Statut : {resultat.statut}
                        </p>
                    )}
                </div>

                <button
                    onClick={onClose}
                    disabled={submitting}
                    className="w-full py-3 sm:py-4 bg-green-800 text-white rounded-xl hover:bg-green-700 transition font-serif font-semibold text-sm sm:text-lg disabled:opacity-50"
                >
                    Terminer
                </button>
            </div>
        );
    };

    // =======================================================================
    // RENDU PRINCIPAL
    // =======================================================================
    if (!isOpen || !evaluation) return null;

    const isWelcome = showWelcome && !isSubmitted;
    const isLast = currentIndex === totalQuestions - 1;
    const isFirst = currentIndex === 0;
    const progress = totalQuestions > 0 ? (answeredCount / totalQuestions) * 100 : 0;
    const displayTime = sessionDateLimite ? formatTime(remainingSeconds) : formatTime(timeSpent);
    const timeCritical = sessionDateLimite && remainingSeconds <= 60;

    return (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-0 sm:p-4">
            <div
                ref={containerRef}
                className="bg-white w-full h-full sm:rounded-2xl sm:max-w-5xl sm:h-[92vh] flex flex-col overflow-hidden shadow-2xl"
            >
                {/* ---------- Header ---------- */}
                <div className="bg-gradient-to-r from-green-800 to-green-700 text-white px-3 py-2.5 sm:px-6 sm:py-4 flex items-center justify-between flex-shrink-0">
                    <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                        <button onClick={onClose} className="p-1.5 hover:bg-white/20 rounded-lg transition flex-shrink-0">
                            <X className="w-5 h-5" />
                        </button>
                        <Award className="w-4 h-4 sm:w-5 sm:h-5 text-green-200 flex-shrink-0" />
                        <span className="font-serif font-bold text-xs sm:text-base truncate">
                            {formationTitre || evaluation?.titre || 'Évaluation'}
                        </span>
                        {isDemo && (
                            <span className="text-[8px] sm:text-[10px] font-bold text-amber-900 bg-amber-300 px-1.5 py-0.5 rounded-full flex-shrink-0">
                                DÉMO
                            </span>
                        )}
                        <span className="hidden sm:inline text-[10px] text-white/70 bg-white/20 px-2.5 py-0.5 rounded-full flex-shrink-0">
                            Module {moduleOrdre}/{totalModules}
                        </span>
                    </div>

                    <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
                        {!isWelcome && !isSubmitted && (
                            <span className={`flex items-center gap-1 text-xs sm:text-sm font-mono px-2 py-1 rounded-full ${
                                timeCritical ? 'bg-red-500 animate-pulse' : 'bg-white/20'
                            }`}>
                                <Clock className="w-3 h-3 sm:w-4 sm:h-4" />
                                {displayTime}
                            </span>
                        )}
                        <button onClick={toggleFullscreen} className="p-1.5 hover:bg-white/20 rounded-lg transition">
                            {isFullscreen ? <Minimize2 className="w-5 h-5" /> : <Maximize2 className="w-5 h-5" />}
                        </button>
                    </div>
                </div>

                {sessionError && !isWelcome && !isSubmitted && (
                    <div className="bg-amber-50 border-b border-amber-200 px-4 py-2 flex items-center gap-2 flex-shrink-0">
                        <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0" />
                        <span className="text-[11px] sm:text-sm text-amber-800">{sessionError}</span>
                    </div>
                )}

                {/* ---------- Body ---------- */}
                <div className="flex-1 overflow-y-auto p-3 sm:p-8 bg-gray-50">
                    {isWelcome ? renderWelcome() :
                     isSubmitted ? renderEndScreen() :
                     totalQuestions === 0 ? (
                        <div className="flex flex-col items-center justify-center h-full text-gray-500">
                            <AlertCircle className="w-12 h-12 mb-4 text-gray-300" />
                            <p className="text-sm sm:text-lg font-serif">Aucune question dans cette évaluation</p>
                        </div>
                     ) : (
                        <div className="space-y-4 sm:space-y-6 max-w-3xl mx-auto">
                            <div className="flex justify-between items-center text-xs sm:text-base">
                                <span className="font-serif text-gray-700">
                                    Question {currentIndex + 1} / {totalQuestions}
                                </span>
                                <span className="text-gray-500 text-[10px] sm:text-sm">
                                    {answeredCount}/{totalQuestions} répondues
                                </span>
                            </div>

                            <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                                <div
                                    className="h-full bg-green-700 rounded-full transition-all duration-500"
                                    style={{ width: `${progress}%` }}
                                />
                            </div>

                            {/* Pastilles de navigation rapide */}
                            <div className="flex flex-wrap gap-1.5">
                                {questions.map((_, i) => (
                                    <button
                                        key={i}
                                        onClick={() => setCurrentIndex(i)}
                                        className={`w-7 h-7 sm:w-8 sm:h-8 rounded-lg text-[10px] sm:text-xs font-bold transition ${
                                            i === currentIndex
                                                ? 'bg-green-800 text-white'
                                                : hasAnswer(answers[i])
                                                    ? 'bg-green-100 text-green-800 border border-green-300'
                                                    : 'bg-white text-gray-400 border border-gray-200 hover:border-gray-400'
                                        }`}
                                    >
                                        {i + 1}
                                    </button>
                                ))}
                            </div>

                            {renderQuestion(currentQuestion, currentIndex)}

                            <div className="flex items-center gap-2 pt-4 border-t border-gray-200">
                                <button
                                    onClick={goToPrev}
                                    disabled={isFirst}
                                    className="flex-1 px-2 py-2.5 sm:px-5 sm:py-3 bg-white border-2 border-gray-200 text-gray-700 rounded-xl hover:bg-gray-50 disabled:opacity-30 transition font-serif text-xs sm:text-base truncate"
                                >
                                    <ChevronLeft className="w-4 h-4 inline mr-1" /> Précédent
                                </button>

                                {isLast ? (
                                    <button
                                        onClick={() => doSubmit(false)}
                                        disabled={submitting}
                                        className="flex-1 px-2 py-2.5 sm:px-8 sm:py-3 bg-green-800 text-white rounded-xl hover:bg-green-700 transition flex items-center justify-center gap-2 font-serif font-semibold text-xs sm:text-base disabled:opacity-50"
                                    >
                                        <Send className="w-4 h-4" />
                                        {isDemo ? 'Terminer la démo' : 'Soumettre'}
                                    </button>
                                ) : (
                                    <button
                                        onClick={goToNext}
                                        className="flex-1 px-2 py-2.5 sm:px-5 sm:py-3 bg-white border-2 border-gray-200 text-gray-700 rounded-xl hover:bg-gray-50 transition font-serif text-xs sm:text-base truncate"
                                    >
                                        Suivant <ChevronRight className="w-4 h-4 inline ml-1" />
                                    </button>
                                )}
                            </div>

                            {answeredCount < totalQuestions && (
                                <p className="text-[10px] sm:text-sm text-amber-600 text-center font-serif">
                                    Il reste {totalQuestions - answeredCount} question(s) sans réponse.
                                </p>
                            )}
                        </div>
                     )}
                </div>

                {/* ---------- Footer ---------- */}
                <div className="bg-white border-t border-gray-200 px-3 py-2 sm:px-6 sm:py-3 flex justify-between items-center flex-shrink-0">
                    <span className="text-[10px] sm:text-sm text-gray-500 font-serif">
                        Module {moduleOrdre}/{totalModules}
                    </span>
                    <span className="text-[9px] sm:text-xs text-gray-500">
                        {isSubmitted
                            ? (isDemo ? 'Démo terminée' : 'Transmis — en attente de correction')
                            : isDemo ? 'Mode démonstration' : 'Épreuve en cours'}
                        {sessionDateLimite && !isSubmitted && ` — ${displayTime}`}
                    </span>
                </div>
            </div>
        </div>
    );
}